/**
 * SearchableSelect — Single-select dropdown with search using Popover + Command.
 *
 * Supports both flat options and grouped options (rendered with CommandGroup headings).
 */

import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface SearchableSelectOption {
  value: string
  label: string
  description?: string
}

export interface SearchableSelectGroup {
  label: string
  options: SearchableSelectOption[]
}

interface SearchableSelectProps {
  /** Flat option list (use this OR groups, not both) */
  options?: SearchableSelectOption[]
  /** Grouped option list — each group gets a heading */
  groups?: SearchableSelectGroup[]
  value: string | null
  onValueChange: (value: string | null) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  /** Show a "None" option that sets value to null */
  allowClear?: boolean
  className?: string
}

export function SearchableSelect({
  options,
  groups,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  disabled = false,
  allowClear = false,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)

  // Flatten all options for label lookup
  const allOptions = useMemo(() => {
    if (groups) return groups.flatMap((g) => g.options)
    return options ?? []
  }, [options, groups])

  const selectedOption = allOptions.find((o) => o.value === value)

  const renderItem = (option: SearchableSelectOption) => (
    <CommandItem
      key={option.value}
      value={option.label}
      onSelect={() => {
        onValueChange(option.value)
        setOpen(false)
      }}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4',
          value === option.value ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div className="flex flex-col min-w-0">
        <span className="truncate">{option.label}</span>
        {option.description && (
          <span className="text-xs text-muted-foreground truncate">
            {option.description}
          </span>
        )}
      </div>
    </CommandItem>
  )

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedOption && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty>{emptyMessage}</CommandEmpty>

            {/* Clear option */}
            {allowClear && (
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onValueChange(null)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === null ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="text-muted-foreground">None</span>
                </CommandItem>
              </CommandGroup>
            )}

            {/* Grouped rendering */}
            {groups?.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.options.map(renderItem)}
              </CommandGroup>
            ))}

            {/* Flat rendering (when no groups) */}
            {!groups && (
              <CommandGroup>
                {(options ?? []).map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
